import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OrderIndexDesktop from './OrderIndexDesktop';
import './OrderPage.scss';

import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import AppLoader from '../../../components/ui/loaders/AppLoader/AppLoader';
import Page from '../../../components/common/Page/Page';
import Container from '../../../components/ui/layouts/Container/Container';
import { MobileOnly, TabletAndUpper } from '../../../components/common/Media/MediaHelpers';
import { getOrderThunk } from '../../../store/order/order-thunk';
import OrderIndexMobile from './mobile/OrderIndexMobile';
import OrderCollisionModal from './components/OrderCollisionModal/OrderCollisionModal';
import IconArrowLeft from '../../../components/ui/icons/account/IconArrowLeft';
import useWindowSize from '../../../hooks/useWindowSize';
import { isClient } from '../../../helpers/common';
import { clearPaymentServerData, setCatGetCartAfterPost } from '../../../store/order/order-reducer';
import OrderIsEmpty from './components/OrderIsEmpty/OrderIsEmpty';
import { RouteNames } from '../../../routes';
import appToastr from '../../../services/appToastr';
import OrderConfirmOfferModal from './OrderConfirmOfferModal/OrderConfirmOfferModal';
import { OrderPaymentRedirectType, OrderPaymentResponse } from '../../../types/order';
import { collectAllErrorToString } from '../../../helpers/network';
import ErrorModal from '../../../components/ui/modals/ErrorModal';
import { translate } from '../../../helpers/translatioHelpers';
import orderApi from '../../../api/orderApi';

const setPaymentDebugData = (key: string, value: unknown) => {
   if (!isClient) return;

   try {
      sessionStorage.setItem(key, JSON.stringify({
         timestamp: new Date().toISOString(),
         value,
      }));
   } catch (error) {
      console.error('[payment-debug] sessionStorage error', error);
   }
};

const PAYMENT_REDIRECT_DELAY_MS = 1000;

type ConfirmOrderResponse = {
   success?: boolean;
   uuid?: string;
   message?: string;
   data?: {
      success?: boolean;
      uuid?: string;
      message?: string;
   };
};

const OrderPageBase = () => {
   const {
      serverOrderData,
      storingOnServerData,
      isLoading,
      canGetCartAfterPost,
   } = useAppSelector(state => state.order);

   const [loaded, setLoaded] = useState(false);
   const dispatch = useAppDispatch();
   const history = useHistory();
   const { isMobile } = useWindowSize();
   const [mobilePageStep, setMobilePageStep] = useState<number>();
   const { paymentServerData, paymentServerDataError } = useAppSelector(state => state.order);
   const [showConfirmModal, setShowConfirmModal] = useState(false);

   const [orderIdForConfirm, setOrderIdForConfirm] = useState<string | null>(null);
   const [showPaymentUnavilable, setShowPaymentUnavailable] = useState<boolean>(false);
   const pendingRedirectRef = useRef<OrderPaymentRedirectType | null>(null);
   const [paymentRedirectData, setPaymentRedirectData] = useState<OrderPaymentRedirectType | null>(null);
   const [isConfirming, setIsConfirming] = useState(false);

   const [t] = useTranslation();

   useEffect(() => {
      dispatch(getOrderThunk());

      return () => {
         dispatch(clearPaymentServerData());
         setShowConfirmModal(false);
         setShowPaymentUnavailable(false);
         pendingRedirectRef.current = null;
         setPaymentRedirectData(null);
         setIsConfirming(false);
      };
   }, []);

   useEffect(() => {
      if (!isLoading && !loaded) setLoaded(true);
   }, [isLoading]);

   useEffect(() => {
      setMobilePageStep(isMobile ? 1 : null);
   }, [isMobile]);

   useEffect(() => {
      if (isClient) window.scrollTo({ top: 0 });
   }, [mobilePageStep]);

   const formRef = useRef<HTMLFormElement | null>(null);

   useEffect(() => {
      checkPaymentDataFromServer(paymentServerData);
   }, [paymentServerData]);

   const redirectToPayment = (link: string) => {
      window.location.href = link;
   };

   useEffect(() => {
      if (paymentRedirectData?.method === 'post' && paymentRedirectData?.link) {
         setTimeout(() => {
            formRef.current?.submit();
         }, 100);
      }
   }, [paymentRedirectData]);

   const startRedirect = (redirectData?: OrderPaymentRedirectType | null) => {
      if (!redirectData?.link) return;

      setPaymentDebugData('payment-debug:startRedirect', redirectData);
      console.log('[payment-debug] startRedirect', redirectData);

      if (redirectData.method === 'post') {
         setTimeout(() => {
            setPaymentRedirectData(redirectData);
         }, PAYMENT_REDIRECT_DELAY_MS);
      } else {
         setTimeout(() => {
            redirectToPayment(redirectData.link);
         }, PAYMENT_REDIRECT_DELAY_MS);
      }
   };

   const checkPaymentDataFromServer = (paymentServerData: OrderPaymentResponse) => {
      const redirectData = paymentServerData?.redirect as OrderPaymentRedirectType;
      console.log(redirectData, paymentServerData);

      if (!paymentServerData) return;

      //Todo: need to refactor
      if (redirectData?.link) {
         console.log(redirectData);
         if (redirectData?.publicOffer) {
            setPaymentRedirectData(null);
            pendingRedirectRef.current = redirectData;
            setOrderIdForConfirm(redirectData?.id);
            setShowConfirmModal(true);
         } else {
            startRedirect(redirectData);
         }
      } else {
         if (paymentServerData?.success) {
            history.push(`${RouteNames.ORDER_SUCCESS}?can-confirm=${paymentServerData?.canConfirm}`);
            sessionStorage.setItem('paymentServerData', JSON.stringify(paymentServerData));
         } else {
            setShowPaymentUnavailable(true);
         }
      }
   };

   useEffect(() => {
      if (paymentServerDataError) {
         const message = paymentServerDataError?.message;
         appToastr.clear();
         appToastr.error(`Помилка при оплаті ${message && ': ' + message}`);
         dispatch(clearPaymentServerData());
      }
   }, [paymentServerDataError]);

   useEffect(() => {
      if (!canGetCartAfterPost) {
         setTimeout(() => {
            dispatch(setCatGetCartAfterPost(true));
         }, 3000);
      }
   }, [canGetCartAfterPost]);

   const onMobileSubmit = () => {
      if (mobilePageStep === 1) setMobilePageStep(2);
   };

   const orderIsEmpty = !serverOrderData || typeof serverOrderData !== 'object';

   const onOrderConfirm = async () => {
      if (!orderIdForConfirm || isConfirming) return;

      setIsConfirming(true);

      try {
         const response = await orderApi.confirmOrder(orderIdForConfirm) as ConfirmOrderResponse;
         const resolvedResponse = response?.data || response;
         setPaymentDebugData('payment-debug:offerAgreeResponse', response);
         console.log('[offer-agree] response', response);
         const isConfirmSuccess = resolvedResponse?.success === true;

         if (isConfirmSuccess) {
            setShowConfirmModal(false);
            const redirectData = pendingRedirectRef.current || (paymentServerData?.redirect as OrderPaymentRedirectType);
            startRedirect(redirectData);
         } else {
            if (resolvedResponse?.message) {
               appToastr.error(`Помилка при оплаті: ${resolvedResponse.message}`);
            }
            setShowPaymentUnavailable(true);
         }
      } catch (error) {
         setPaymentDebugData('payment-debug:offerAgreeError', error);
         console.error('[offer-agree] error', error);
         appToastr.error(`Помилка при оплаті ${collectAllErrorToString(error)}`);
      } finally {
         setIsConfirming(false);
      }
   };

   return (
      <Page
         className={`order-page ${isMobile ? 'order-page--mobile' : ''}`}
         hideSearchForm
         hideSearchHeader={isMobile}
      >
         {showConfirmModal && (
            <OrderConfirmOfferModal
               onClose={() => {
                  if (isConfirming) return;
                  setPaymentRedirectData(null);
                  pendingRedirectRef.current = null;
                  setOrderIdForConfirm(null);
                  setShowConfirmModal(false);
               }}
               onConfirm={onOrderConfirm}
               isConfirming={isConfirming}
            />
         )}

         {showPaymentUnavilable && (
            <ErrorModal
               onClose={() => {setShowPaymentUnavailable(false);}}
               text={translate('Платіж неможливий')}
            />
         )}

         {paymentRedirectData?.link && (
            <form action={paymentRedirectData.link} method="post" target="_self" ref={formRef}>
               <input type="hidden" name="bodyRequest"
                      value={JSON.stringify(paymentRedirectData?.data?.bodyRequest)} />
               <input type="hidden" name="typeRequest" value="json" />
               <input type="submit" value="Submit" style={{ display: 'none' }} />
            </form>
         )}

         <Container className={'order-page__container'}>
            {!loaded ?
               <div className={'order-page__container-loading'}><AppLoader full fixed={isMobile} /></div> : (
                  <>
                     {storingOnServerData && storingOnServerData?.success === false &&
                        <OrderCollisionModal storingOnServerData={storingOnServerData} />}

                     <MobileOnly>
                        <div className={'order-page__mobile-header'}>
                           <div className={'order-page__mobile-header-arrow'}>
                              <IconArrowLeft onClick={() => {
                                 if (mobilePageStep === 2) setMobilePageStep(1);
                                 else history.goBack();
                              }} />
                           </div>

                           <h1>{t('Оформлення бронювання')}</h1>
                        </div>
                     </MobileOnly>

                     {orderIsEmpty
                        ? <OrderIsEmpty />
                        : (
                           <>
                              <MobileOnly>
                                 <OrderIndexMobile
                                    step={mobilePageStep}
                                    serverOrderData={serverOrderData}
                                    onSubmit={onMobileSubmit}
                                 />
                              </MobileOnly>

                              <TabletAndUpper>
                                 <OrderIndexDesktop serverOrderData={serverOrderData} />
                              </TabletAndUpper>
                           </>
                        )}

                     {isLoading && <AppLoader full />}
                  </>
                )}
          </Container>
      </Page>
   );
};

OrderPageBase.getInitialProps = ({ store }) => {
   // if (store?.getState()?.order?.canGetCartAfterPost) {
   //     return store
   //         .dispatch(getOrderThunk())
   //         .then(unwrapResult)
   //         .catch(error => {});
   // }
};

export default OrderPageBase;
